import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PermissionService } from '@abp/ng.core';

// Primeng
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageService } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { ToastModule } from 'primeng/toast';
import { DEFAULT_TIMEZONE } from '../../../../api/common';
import * as dayjs from 'dayjs';
// app
import { SectionCardComponent, UserPickerComponent } from '../../../../components/shared/shared.component';

import { EnumPipe } from '../../../../shared/pipe/enum.pipe';
import { LegacyReviewService } from './../../../../services/legacy-review-service';
import { MatterService } from './../../../../services/matter.service';
import { EntityType } from '../../../../api/enum/entity-type';
import { ReviewStatusList } from '../../../../api/enum/review-status';
import { LegacyReview } from '../../../../api/data-model';
import { CompanyService, ForeignNationalService } from '../../../../services';
import { PTDatePipe } from '../../../../shared/pipe';

@Component({
  selector: 'app-legacy-review',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DropdownModule,
    SectionCardComponent,
    CheckboxModule,
    DialogModule,
    EditorModule,
    EnumPipe,
    UserPickerComponent,
    InputTextModule,
    RadioButtonModule,
    InputNumberModule,
    DrawerModule,
    ToastModule,
    PTDatePipe
  ],
  templateUrl: './legacy-review.component.html',
  styleUrl: './legacy-review.component.scss',
  providers: [MatterService, LegacyReviewService, MessageService, CompanyService, ForeignNationalService],
})
export class LegacyReviewComponent implements OnInit, OnDestroy, OnChanges {
  @Input() entityType: number | null = null;
  @Input() entityId: string | null = null;
  @Output() saved = new EventEmitter<LegacyReview>();

  visible: boolean = false;
  loading: boolean = false;
  saving: boolean = false;
  isEditMode: boolean = false;

  // Actual entity ID (database ID for Matter)
  actualEntityId: string | null = null;

  legacyReviewForm: FormGroup;
  currentLegacyReview: LegacyReview | null = null;

  statusOptions = ReviewStatusList;


  statusIconConfig = {
    0: 'pi pi-exclamation-circle', // Not Started
    1: 'pi pi-exclamation-circle', // In Progress
    2: 'pi pi-check-circle', // Completed
    3: 'pi pi-exclamation-circle', // Report an Issue
  };

  destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly permissionService: PermissionService,
    private readonly legacyReviewService: LegacyReviewService,
    private readonly matterService: MatterService,
    private readonly messageService: MessageService,
    private readonly companyService: CompanyService,
    private readonly foreignNationalService: ForeignNationalService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.legacyReviewForm = this.fb.group({
      notes: ['', Validators.maxLength(1024)],
      status: [0, Validators.required],
      creator: [''],
      lastModifier: ['']
    });
  }

  ngOnInit() {
    this.loadDataOnInit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['entityType'] || changes['entityId']) {
      this.loadDataOnInit();
    }
  }

  getButtonIcon(): string {
    if (!this.currentLegacyReview) {
      return 'pi pi-exclamation-circle';
    }
    const status = this.currentLegacyReview.status;
    return this.statusIconConfig[status as keyof typeof this.statusIconConfig] || 'pi pi-exclamation-circle';
  }

  getButtonSeverity(): 'danger' | 'info' | 'success' | 'warn' | undefined {
    if (!this.currentLegacyReview) {
      return 'info';
    }

    const status = this.currentLegacyReview.status;
    switch (status) {
      case 0: // Not Started
        return 'info';
      case 1: // In Progress
        return 'warn';
      case 2: // Completed
        return 'success';
      case 3: // Report an Issue
        return 'danger';
      default:
        return 'info';
    }
  }

  getButtonTitle(): string {
    return 'INSZoom Data Migration Review';

    /*if (!this.currentLegacyReview) {
      return 'INSZoom Data Migration Review';
    }

    const statusLabel = this.statusOptions.find(opt => opt.value === this.currentLegacyReview?.status)?.label;
    return `INSZoom Data Migration Review - ${statusLabel || 'Not Started'}`;*/
  }

  private loadDataOnInit() {
    if (this.entityType == null || !this.entityId) {
      return;
    }


    this.loadActualEntityIdForInit();
  }

  private loadActualEntityIdForInit() {
    this.actualEntityId = this.entityId;

    switch (this.entityType) {
      case EntityType.Matter:
        this.matterService.getMatterByCode(this.entityId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (matter) => {
              if (matter?.id) {
                this.actualEntityId = matter.id;
                this.loadExistingReviewForInit();
              }
            },
            error: (error) => {
              console.error('Error fetching matter for init:', error);
            }
          });
        break;

      case EntityType.Company:
        this.companyService.getIdByCode(this.entityId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (company) => {
              if (company?.id) {
                this.actualEntityId = company.id;
                this.loadExistingReviewForInit();
              }
            },
            error: (error) => {
              console.error('Error fetching company for init:', error);
            }
          });
        break;

      case EntityType.ForeignNational:
        this.foreignNationalService.getFNByCode(this.entityId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (foreignNational) => {
              if (foreignNational?.id) {
                this.actualEntityId = foreignNational.id;
                this.loadExistingReviewForInit();
              }
            },
            error: (error) => {
              console.error('Error fetching foreign national for init:', error);
            }
          });
        break;

      default:
        this.loadExistingReviewForInit();
        break;
    }
  }

  private loadExistingReviewForInit() {
    if (!this.actualEntityId) {
      return;
    }

    const reviewId = this.actualEntityId;

    this.legacyReviewService.getLegacyReview(reviewId, this.entityType!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (review) => {
          this.currentLegacyReview = review;
          this.isEditMode = true;


          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          if (error.status === 404) {
            this.currentLegacyReview = {
              entityType: this.entityType!,
              entityId: this.actualEntityId!,
              status: 0,
              notes: '',
              creatorBy: '',
              lastModifierBy: '',
              creationTime: '',
              lastModificationTime: ''
            };

            this.changeDetectorRef.detectChanges();
          } else {
            console.error('Error loading legacy review for init:', error);
          }
        }
      });
  }



  open() {

    if (this.entityType == null || !this.entityId) {
      console.warn('Missing entity information:', { type: this.entityType, id: this.entityId });
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Missing entity information'
      });
      return;
    }

    this.loading = true;
    this.visible = true;
    this.legacyReviewForm.reset({
      notes: '',
      status: 0
    });
    this.loadActualEntityId();
  }

  private loadActualEntityId() {
    this.actualEntityId = this.entityId;

    switch (this.entityType) {
      case EntityType.Matter:
        this.matterService.getMatterByCode(this.entityId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (matter) => {
              if (matter?.id) {
                this.actualEntityId = matter.id;
              }
              this.loadExistingReview();
            },
            error: (error) => {
              console.error('Error fetching matter:', error);
              this.handleEntityLoadError('Matter');
            }
          });
        break;

      case EntityType.Company:
        this.companyService.getIdByCode(this.entityId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (company) => {
              if (company?.id) {
                this.actualEntityId = company.id;
              }
              this.loadExistingReview();
            },
            error: (error) => {
              console.error('Error fetching company:', error);
              this.handleEntityLoadError('Company');
            }
          });
        break;

      case EntityType.ForeignNational:
        this.foreignNationalService.getFNByCode(this.entityId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (foreignNational) => {
              if (foreignNational?.id) {
                this.actualEntityId = foreignNational.id;
              }
              this.loadExistingReview();
            },
            error: (error) => {
              console.error('Error fetching foreign national:', error);
              this.handleEntityLoadError('Foreign National');
            }
          });
        break;

      default:
        this.loadExistingReview();
        break;
    }
  }

formatDate(dateString: string): string {
  if (!dateString) return '';

  let value = dateString;

  if (typeof value === 'string') {
    const hasTimezoneInfo = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})/.test(value);
    value = hasTimezoneInfo ? value : `${value}Z`;
  }

  const dayjsInstance = dayjs.default || dayjs;
  return dayjsInstance(value)
    .tz(DEFAULT_TIMEZONE)
    .format('MM/DD/YYYY HH:mm:ss');
}
  private handleEntityLoadError(entityType: string) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to get ${entityType} information`
    });
    this.loading = false;
    this.visible = false;
  }

  private loadExistingReview() {
    if (!this.actualEntityId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Cannot determine entity ID'
      });
      this.visible = false;
      return;
    }

    this.loading = true;
    this.isEditMode = false;

    const reviewId = this.actualEntityId;

    this.legacyReviewService.getLegacyReview(reviewId, this.entityType!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (review) => {
          this.loading = false;
          this.currentLegacyReview = review;
          this.isEditMode = true;
          this.patchFormValues(review);
        },
        error: (error) => {
          if (error.status === 404) {
            this.initNewForm();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load data'
            });
          }
          this.loading = false;
        }
      });
  }

  private patchFormValues(review: LegacyReview) {
    this.legacyReviewForm.patchValue({
      notes: review.notes || '',
      status: review.status || 0,
      creatorBy: review.creatorBy || '',
      lastModifierBy: review.lastModifierBy || ''
    });
  }

  private initNewForm() {
    this.currentLegacyReview = {
      entityType: this.entityType!,
      entityId: this.actualEntityId!,
      status: 0,
      notes: '',
      creatorBy: '',
      lastModifierBy: '',
      creationTime: '',
      lastModificationTime: ''
    };

    this.legacyReviewForm.reset({
      notes: '',
      status: 0
    });
  }

  onCancel() {
    this.visible = false;
    this.legacyReviewForm.reset();
    this.actualEntityId = null;
    this.isEditMode = false;
  }

  onSave() {
    if (!this.actualEntityId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Cannot determine entity ID'
      });
      return;
    }

    if (this.legacyReviewForm.invalid) {
      this.markFormGroupTouched(this.legacyReviewForm);
      return;
    }

    this.saving = true;

    const formValue = this.legacyReviewForm.value;
    if(formValue.notes.length > 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Notes too long (max 1024 characters)'
      });
      this.saving = false;
      return;
    }

    const reviewToSave: LegacyReview = {
      entityType: this.entityType!,
      entityId: this.actualEntityId!,
      status: formValue.status,
      notes: formValue.notes,
      creatorBy: this.isEditMode && this.currentLegacyReview ? this.currentLegacyReview.creatorBy : '',
      lastModifierBy: this.isEditMode && this.currentLegacyReview ? this.currentLegacyReview.lastModifierBy : '',
      creationTime: this.isEditMode && this.currentLegacyReview ? this.currentLegacyReview.creationTime : '',
      lastModificationTime: this.isEditMode && this.currentLegacyReview ? this.currentLegacyReview.lastModificationTime : ''
    };

    const reviewId = `${this.entityType}_${this.actualEntityId}`;

    this.legacyReviewService.updateLegacyReview(reviewToSave)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (savedReview) => {
          this.saving = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.isEditMode ? 'Update successful' : 'Create successful'
          });

          this.currentLegacyReview = savedReview;
          this.isEditMode = true;

          this.visible = false;
          this.saved.emit(savedReview);
        },
        error: (error) => {
          this.saving = false;
          console.error('Save error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Save failed: ' + (error.message || 'Unknown error')
          });
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  canEdit(): boolean {
    return true;
  }

  getDebugInfo(): string {
    let entityTypeName = 'Unknown';
    switch (this.entityType) {
      case EntityType.Matter:
        entityTypeName = 'Matter';
        break;
      case EntityType.Company:
        entityTypeName = 'Company';
        break;
      case EntityType.ForeignNational:
        entityTypeName = 'ForeignNational';
        break;
    }

    return `Type: ${entityTypeName} (${this.entityType}), ID: ${this.entityId}, Actual ID: ${this.actualEntityId || 'Not loaded'}`;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
